import { installPaintMaterial, updatePaintMaterial } from './item-paint.js';
import { LiveAnnouncer, AnnouncerAudio } from "./live-announcer.js";
import { applyRenderState } from "./render-state.js";
import * as T from "three";
import { RagdollMotion, gibVelocity } from "./live-ragdolls.js";
import { LiveSprays } from "./live-sprays.js";
import { LoadingSpinners } from "./loading-spinners.js";
import { LiveSprite } from "./live-sprites.js";
import { LiveSounds } from "./live-sounds.js";
import { LiveParticles } from "./live-particles.js";
import { kartModel, kartVisualState, poseKart } from "./live-karts.js";
import { LiveParenting } from "./live-parenting.js";
import { LiveBodygroups } from './live-bodygroups.js';
import { createWorldText, updateWorldText } from "./live-world-text.js";
import { ClipCache, AnimationPlayer } from "./live-animation.js";
import { createSkeleton } from "./model-skeleton.js";
import { AnimatedBounds, skinInfluenceBounds } from "./model-bounds.js";
import { lightingMaterial } from "./lighting.js";
import { Component } from "./live-codec.js";
import { LiveTimeline, interpolateTransform } from "./live-timeline.js";
import { LiveTimings } from "./live-metrics.js";

/** Render-only mirror. The worker remains the owner of authoritative state. */
export class LiveScene {
    constructor(view, rpc, { now = () => performance.now(), concurrency = 6, audio = true, measureNow = () => performance.now() } = {}) {
        this.view = view; this.rpc = rpc; this.now = now; this.concurrency = concurrency;
        this.parenting = new LiveParenting();
        this.bodygroups = new LiveBodygroups(rpc, entity => { this.refreshVisual(entity); this.pump(); }, message => view.diagnostic?.(message));
        this.particles = new LiveParticles(view, rpc);
        this.sprays = new LiveSprays(view, rpc);
        this.sounds = audio ? new LiveSounds(view.audioOutput, rpc, { diagnostic: message => view.diagnostic?.(message) }) : null;
        this.announcerAudio = audio ? new AnnouncerAudio(this.sounds, rpc, { diagnostic: message => view.diagnostic?.(message) }) : null;
        this.announcer = audio ? new LiveAnnouncer({ emit: cue => void this.announcerAudio.emit(cue), prepare: entry => void this.announcerAudio.prepare(entry) }) : null;
        this.gibs = new Map(); this.entities = new Map(); this.pendingModels = new Set(); this.dictionary = new Map(); this.timeline = new LiveTimeline();
        this.root = new T.Group(); view.scene.add(this.root);
        this.skyRoot = new T.Group(); view.skyScene?.add(this.skyRoot);
        this.running = 0; this.closed = false; this.epoch = null; this.owned = false;
        this.clips = new ClipCache(rpc, message => view.diagnostic?.(message));
        this.modelLoads = new Map(); this.sharedGeometry = new Map(); this.hidden = new Map();
        this.tauntPropModels = new Map();
        this.loadingSpinners = new LoadingSpinners([this.root, this.skyRoot]);
        this.measureNow = measureNow; this.pendingWorkMs = 0;
        this.timings = new LiveTimings(["applyMs", "modelSetupMs", "animatedBoundsMs", "frameUpdateMs", "frameWorkMs"]);
    }
    ownMapEntities() {
        if (this.owned) return;
        this.owned = true;
        this.view.mapParticles?.dispose(); this.view.mapParticles = null;
        if (this.view.lighting) {
            this.mapLights = [...this.view.lighting.dynamic];
            for (const light of this.mapLights) { this.hidden.set(light.object, light.object.visible); light.object.visible = false; }
            this.view.lighting.dynamic = [];
            this.view.lighting.refreshMasks();
        }
        for (const item of this.view.brushPrototypes ?? this.view.meshes ?? []) if (item.model > 0) {
            this.hidden.set(item.mesh, item.mesh.visible); item.liveHidden = true; item.mesh.visible = false;
        }
        for (const scene of [this.view.scene, this.view.skyScene]) scene?.traverse(object => {
            if (object.userData.liveDynamicProp) { this.hidden.set(object, object.visible); object.visible = false; }
        });
    }
    apply(message) {
        if (this.closed) return;
        const started = this.measureNow();
        try { this.applyState(message); }
        finally { this.recordWork("applyMs", this.measureNow() - started); }
    }
    recordWork(label, milliseconds) {
        this.timings.record(label, milliseconds);
        this.view.performanceOverlay?.details?.record(label === 'applyMs' ? 'apply' : 'models', milliseconds);
        if (Number.isFinite(milliseconds) && milliseconds >= 0) this.pendingWorkMs += milliseconds;
    }
    measureSetup(action) {
        const started = this.measureNow();
        try { return action(); }
        finally { this.recordWork("modelSetupMs", this.measureNow() - started); }
    }
    frame(now) {
        if (this.closed) return;
        const started = this.measureNow();
        this.update(now);
        const elapsed = this.measureNow() - started;
        this.timings.record("frameUpdateMs", elapsed);
        this.timings.record("frameWorkMs", this.pendingWorkMs + elapsed);
        this.pendingWorkMs = 0;
    }
    diagnostics() {
        let placeholders = 0, pendingModels = 0, skeletons = 0, bones = 0;
        for (const entity of this.entities.values()) {
            if (entity.loadingPlaceholder) placeholders++;
            if (entity.pending) pendingModels++;
            if (entity.rig) { skeletons++; bones += entity.rig.bones.length; }
        }
        return { particles: this.particles.diagnostics(), timings: this.timings.summary(), entities: this.entities.size, placeholders,
            pendingModels, modelsInFlight: this.running, skeletons, bones,
            sharedGeometries: this.sharedGeometry.size, clipEntries: this.clips.entries.size, clipBytes: this.clips.bytes };
    }
    applyState(message) {
        if (this.epoch !== message.epoch) {
            this.bodygroups.clear();
            for (const entity of this.entities.values()) this.remove(entity);
            this.particles.clear(); this.sounds?.clear(); this.sprays.clear(); this.announcer?.clear(); this.announcerAudio?.clear();
            this.entities.clear(); this.dictionary.clear(); this.modelLoads.clear(); this.tauntPropModels.clear(); this.timeline.reset(); this.epoch = message.epoch;
        }
        this.ownMapEntities();
        if (message.dictionary) this.dictionary = new Map(message.dictionary);
        if (message.replace) {
            const present = new Set(message.changes.map(change => change.key));
            for (const [key, entity] of this.entities) if (!present.has(key)) { this.remove(entity); this.entities.delete(key); }
        }
        for (const key of message.deleted) {
            const entity = this.entities.get(key);
            if (entity) { this.remove(entity); this.entities.delete(key); }
        }
        for (const change of message.changes) {
            let entity = this.entities.get(change.key);
            if (!entity) {
                entity = { key: change.key, object: new T.Group(), resources: new Set(), token: 0 };
                this.entities.set(change.key, entity); this.root.add(entity.object);
            }
            entity.state = change;
        }
        // Resolve owners after all rows have arrived, including snapshots whose
        // weapon row precedes its player. Prop substitution changes this weapon's
        // visual only; the authoritative identity and parenting stay intact.
        this.bodygroups.reconcile(this.entities, this.dictionary);
        if (this.view.debugOptions?.renderLiveEntities !== false) {
            for (const change of message.changes) this.refreshVisual(this.entities.get(change.key));
            this.reconcileTauntProps();
        } else this.visualsDirty = true;
        // Main-thread arrival time avoids comparing independent worker time origins.
        this.timeline.observe(message.sampleTime, this.now());
        this.particles.reconcile(this.entities, this.dictionary);
        this.sounds?.reconcile(this.entities, this.dictionary);
        this.announcer?.accept(message);
        this.sprays.reconcile(this.entities, this.dictionary);
        // State arrivals are consumed immediately; simulation runs once per rendered frame.
        this.pump();
    }
    tauntProp(entity) {
        const effects = entity.state.components[Component.Effects];
        const owner = effects?.ownerIndex ? this.entities.get(`${effects.ownerIndex}:${effects.ownerGeneration}`) : undefined;
        const player = owner?.state.components[Component.Player];
        const item = owner?.state.components[Component.Effects]?.tauntItem;
        const active = player?.weaponIndex === entity.state.index && player?.weaponGeneration === entity.state.generation;
        if (!player || !(player.state & 1) || !((player.conditions?.[0] ?? 0) & 128) || !item || (!active && !(effects.flags & 128))) {
            entity.tauntPropKey = null; return null;
        }
        const key = `${item}:${player.playerClass}`;
        entity.tauntPropKey = key;
        let entry = this.tauntPropModels.get(key);
        if (!entry) {
            if (this.tauntPropModels.size >= 64) this.tauntPropModels.delete(this.tauntPropModels.keys().next().value);
            entry = { path: null }; this.tauntPropModels.set(key, entry);
            const epoch = this.epoch;
            Promise.resolve().then(() => this.rpc('taunt-prop', { item, playerClass: player.playerClass })).then(({ path }) => {
                if (this.closed || this.epoch !== epoch || this.tauntPropModels.get(key) !== entry) return;
                entry.path = path;
                this.reconcileTauntProps(); this.pump();
            }).catch(error => {
                if (!this.closed && this.epoch === epoch && this.tauntPropModels.get(key) === entry)
                    this.view.diagnostic?.(`Taunt prop: ${error.message}`);
            });
        }
        return entry.path;
    }
    reconcileTauntProps() {
        for (const entity of this.entities.values())
            if (entity.tauntPropKey || entity.state.components[Component.Effects]?.ownerIndex) this.refreshVisual(entity);
    }
    refreshVisual(entity) {
        if (this.view.debugOptions?.renderLiveEntities === false) { this.visualsDirty = true; return; }
        const appearance = entity.state.components[Component.Appearance];
        const prop = this.tauntProp(entity);
        const path = prop ?? this.dictionary.get(`1:${appearance.model}`);
        const worldText = entity.state.components[Component.WorldText];
        if (entity.state.components[Component.Teleport]) { this.clearVisual(entity); entity.pending = false; return; }
        const ownViewer = !!appearance.viewerSteamId && appearance.viewerSteamId === this.view.viewerSteamId;
        const body = entity.bodygroupPath === path ? entity.renderBody ?? appearance.body : appearance.body;
        const baseSignature = `${ownViewer}:${JSON.stringify(worldText)}:${path}:${appearance.skin}:${entity.state.components[Component.Light]?.kind ?? 'model'}`;
        const signature = `${baseSignature}:${body}`;
        if (entity.signature === signature) return;
        entity.preserveRig = !!entity.rig && entity.visualBaseSignature === baseSignature;
        if (!entity.preserveRig) this.clearVisual(entity);
        entity.visualBaseSignature = baseSignature; entity.signature = signature; entity.path = path;
        entity.token++;
        if (ownViewer) { entity.object.visible = false; entity.pending = false; return; }
        if (worldText) { entity.worldText = createWorldText(worldText, entity.resources); entity.object.add(entity.worldText); entity.pending = false; return; }
        if (!appearance.model && !prop) { entity.pending = false; return; }
        entity.pending = true; this.pendingModels.add(entity);
        if (entity.preserveRig) return;
        entity.loadingPlaceholder = this.loadingSpinners.add(entity.object);
    }
    clearVisual(entity, preserveRig = false) {
        this.loadingSpinners.remove(entity.loadingPlaceholder); entity.loadingPlaceholder = null;
        this.pendingModels.delete(entity); entity.brushLayer = undefined;
        if (entity.geometryLease) {
            const shared = entity.geometryLease;
            entity.geometryLease = null;
            if (--shared.users === 0) {
                this.sharedGeometry.delete(shared.key);
                for (const geometry of shared.geometries) geometry.dispose();
            }
        }
        if (entity.light) {
            const system = this.view.lighting;
            entity.light.object.target?.removeFromParent(); entity.light.object.dispose();
            if (system) { system.dynamic = system.dynamic.filter(light => light !== entity.light); system.refreshMasks(); }
            entity.light = null;
        }
        if (!preserveRig) {
            entity.rig?.skeleton.dispose(); entity.rig = null; entity.animation = null;
            entity.modelParticleEvents = null; entity.ragdoll = null; entity.gibsStarted = false;
        }
        entity.animatedBounds = null;
        entity.object.clear(); entity.worldText = null; entity.sprite = null; entity.color = undefined; entity.renderMode = undefined;
        if (preserveRig) entity.rig.bones.forEach((bone, index) => {
            if (entity.rig.data.bones[index].parent < 0) entity.object.add(bone);
        });
        for (const resource of entity.resources) resource.dispose();
        entity.resources.clear();
    }
    remove(entity) {
        for (const [key, gib] of this.gibs) if (gib.owner === entity.key) { this.remove(gib); this.gibs.delete(key); }
        entity.token++; entity.pending = false; entity.object.removeFromParent(); this.clearVisual(entity);
    }
    valid(entity, token) { return !this.closed && (this.entities.get(entity.key) === entity || this.gibs.get(entity.key) === entity) && entity.token === token; }
    acquireGeometry(key, meshes) {
        let shared = this.sharedGeometry.get(key);
        if (!shared) {
            const geometries = [];
            try {
                for (const data of meshes) {
                    const geometry = this.view.geometry(data);
                    this.view.resources.delete(geometry); geometries.push(geometry);
                    if (data.skinIndices && data.skinWeights) {
                        geometry.setAttribute("skinIndex", new T.BufferAttribute(data.skinIndices, 4));
                        geometry.setAttribute("skinWeight", new T.BufferAttribute(data.skinWeights, 4));
                    }
                }
            } catch (error) { for (const geometry of geometries) geometry.dispose(); throw error; }
            shared = { key, geometries, skinBounds: [], users: 0 };
            this.sharedGeometry.set(key, shared);
        }
        shared.users++;
        return shared;
    }
    brushModel(path) {
        const source = this.view.brushPrototypes ?? this.view.meshes ?? [];
        // The map owns these immutable prototypes. Build one lookup for the
        // map, including late population/replacement during map setup.
        if (this.brushSource !== source || this.brushSourceLength !== source.length) {
            this.brushSource = source; this.brushSourceLength = source.length;
            this.brushModels = new Map();
            for (const item of source) if (item.model > 0) {
                let model = this.brushModels.get(item.model);
                if (!model) { model = { prototypes: [], inSky: true }; this.brushModels.set(item.model, model); }
                model.prototypes.push(item); model.inSky &&= !!item.inSky;
            }
        }
        return this.brushModels.get(Number(path.slice(1)));
    }
    brushBounds(model) {
        if (!model.bounds) {
            model.bounds = new T.Box3();
            for (const { mesh } of model.prototypes) {
                if (!mesh.geometry.boundingBox) mesh.geometry.computeBoundingBox();
                model.bounds.union(mesh.geometry.boundingBox);
            }
        }
        return model.bounds;
    }
    brushClusters(entity, model, matrix, cacheKey) {
        let cache = entity[cacheKey];
        const visibility = this.view.visibility;
        if (!cache || cache.model !== model || cache.visibility !== visibility || !cache.matrix.equals(matrix)) {
            cache ??= { matrix: new T.Matrix4(), bounds: new T.Box3(), min: [], max: [] };
            cache.model = model; cache.visibility = visibility; cache.matrix.copy(matrix);
            cache.bounds.copy(this.brushBounds(model)).applyMatrix4(matrix);
            cache.clusters = visibility.clustersForBounds(cache.bounds.min.toArray(cache.min), cache.bounds.max.toArray(cache.max));
            entity[cacheKey] = cache;
        }
        return cache.clusters;
    }
    brushInPvs(entity, visible) {
        if (!visible || !entity.path?.startsWith("*") || !this.view.visibility?.clustersForBounds) return true;
        const model = this.brushModel(entity.path);
        if (!model || model.inSky) return true;
        const transform = entity.state.components[Component.Transform];
        const object = this.brushTransform ??= new T.Object3D();
        object.position.fromArray(transform.position); this.view.angles(object, transform.angles);
        object.scale.setScalar(entity.state.components[Component.Appearance].scale); object.updateMatrix();
        return this.view.inPvs(this.brushClusters(entity, model, object.matrix, 'brushLoadClusters'), visible);
    }
    pump() {
        if (this.closed || !this.pendingModels.size || this.running >= this.concurrency || this.view.debugOptions?.renderLiveEntities === false) return;
        const visible = !this.view.debugOptions?.visibility && this.view.camera
            ? this.view.visibility?.visibleClusters?.(this.view.camera.position.toArray()) : null;
        for (const entity of this.pendingModels) {
            if (this.running >= this.concurrency) break;
            if (!entity.pending || !this.brushInPvs(entity, visible)) continue;
            entity.pending = false; this.pendingModels.delete(entity);
            if (!entity.path || entity.state.components[Component.Light]) continue;
            const token = entity.token;
            entity.loading = true;
            this.running++;
            this.load(entity, token).catch(error => {
                if (this.valid(entity, token)) {
                    if (entity.loadingPlaceholder) entity.loadingPlaceholder.visible = false;
                    this.view.diagnostic?.(`${entity.path}: ${error.message}`);
                }
            }).finally(() => { if (entity.token === token) entity.loading = false; this.running--; this.pump(); });
        }
    }
    async load(entity, token) {
        if (entity.path.startsWith("*")) {
            const prototypes = this.brushModel(entity.path)?.prototypes ?? [];
            if (!prototypes.length) throw new Error("Missing BSP submodel");
            const materials = prototypes.filter(item => item.assetTask?.ensure).map(item => item.assetTask.ensure());
            if (materials.length) await Promise.all(materials);
            if (!this.valid(entity, token)) return;
            return this.measureSetup(() => {
                this.clearVisual(entity);
                (prototypes.every(item => item.inSky) ? this.skyRoot : this.root).add(entity.object);
                for (const { mesh } of prototypes) {
                    const material = mesh.material.clone();
                    material.onBeforeCompile = mesh.material.onBeforeCompile;
                    material.customProgramCacheKey = mesh.material.customProgramCacheKey.bind(mesh.material);
                    entity.resources.add(material);
                    const copy = new T.Mesh(mesh.geometry, material);
                    copy.castShadow = mesh.castShadow; copy.receiveShadow = mesh.receiveShadow;
                    entity.object.add(copy);
                }
            });
        }
        if (/\.(?:spr|vmt|vtf)$/.test(entity.path)) {
            const key = `sprite:${entity.path}`;
            if (!this.modelLoads.has(key)) {
                if (this.modelLoads.size >= 64) this.modelLoads.delete(this.modelLoads.keys().next().value);
                this.modelLoads.set(key, this.rpc("sprite", { path: entity.path, s3tc: !!this.view.s3tc }));
            }
            const source = await this.modelLoads.get(key);
            if (!this.valid(entity, token)) return;
            return this.measureSetup(() => {
                this.clearVisual(entity); entity.sprite = new LiveSprite(source, this.view.s3tc, entity.resources);
                entity.object.add(entity.sprite.mesh, entity.sprite.nextMesh);
            });
        }
        const appearance = entity.state.components[Component.Appearance];
        const body = entity.bodygroupPath === entity.path ? entity.renderBody ?? appearance.body : appearance.body;
        const key = `${entity.path}:${appearance.skin}:${body}`;
        if (!this.modelLoads.has(key)) {
            // Bounded decoded-model cache; GPU ownership stays with each entity.
            if (this.modelLoads.size >= 64) this.modelLoads.delete(this.modelLoads.keys().next().value);
            this.modelLoads.set(key, this.rpc("model", { path: entity.path, skin: appearance.skin, body, live: true }));
        }
        const { meshes, skeleton } = await this.modelLoads.get(key);
        if (!this.valid(entity, token)) return;
        const materials = [];
        for (const data of meshes) {
            let material;
            for (const path of data.materials) {
                if (!this.valid(entity, token)) return;
                try { material = await this.view.material(path, this.rpc); break; } catch { /* Try next MDL material directory. */ }
            }
            materials.push(material);
        }
        if (!this.valid(entity, token)) return;
        this.measureSetup(() => {
            const motion = entity.ragdoll;
            this.clearVisual(entity, entity.preserveRig); this.root.add(entity.object);
            if (entity.owner) entity.ragdoll = motion;
            entity.geometryLease = this.acquireGeometry(key, meshes);
            if (!entity.rig && skeleton?.bones.length) {
                entity.rig = createSkeleton(skeleton, entity.object);
                if (skeleton.library) {
                    for (const message of skeleton.library.diagnostics) this.view.diagnostic?.(message);
                    entity.animation = new AnimationPlayer(entity.rig, reference => this.clips.get(reference), message => this.view.diagnostic?.(`${entity.path}: ${message}`));
                }
            }
            meshes.forEach((data, index) => {
                const geometry = entity.geometryLease.geometries[index];
                const source = materials[index];
                const material = source ? source.clone() : new T.MeshLambertMaterial({ color: 0xff00ff });
                if (source) {
                    material.onBeforeCompile = source.onBeforeCompile;
                    material.customProgramCacheKey = source.customProgramCacheKey.bind(source);
                }
                if (source?.userData.paintValues) {
                    material.onBeforeCompile = source.paintBaseHook;
                    material.customProgramCacheKey = () => source.paintBaseKey;
                    installPaintMaterial(material, source.userData.paintValues);
                }
                if (source?.userData.source && !source.userData.source.unlit && this.view.lighting) {
                    if (!source.userData.paintValues) {
                        material.onBeforeCompile = source.sourceShaderHook;
                        material.customProgramCacheKey = () => source.sourceShaderKey;
                    }
                    entity.ambient ??= { cube: { value: Array.from({ length: 6 }, () => new T.Color(1, 1, 1)) },
                        directions: { value: Array.from({ length: 4 }, () => new T.Vector3()) },
                        colors: { value: Array.from({ length: 4 }, () => new T.Color(0, 0, 0)) } };
                    entity.lightOrigin ??= new T.Vector3().fromArray(data.illuminationPosition ?? [0, 0, 0]);
                    lightingMaterial(material, this.view.lighting, { prop: true, ambient: entity.ambient, ...source.sourceEmission });
                }
                entity.resources.add(material);
                let mesh;
                if (entity.rig && data.skinIndices && data.skinWeights) {
                    mesh = new T.SkinnedMesh(geometry, material); mesh.bind(entity.rig.skeleton, new T.Matrix4());
                    const influences = entity.geometryLease.skinBounds[index] ??=
                        skinInfluenceBounds(geometry, entity.rig.bones.length);
                    (entity.animatedBounds ??= []).push(new AnimatedBounds(mesh, influences));
                } else mesh = new T.Mesh(geometry, material);
                mesh.castShadow = true; mesh.receiveShadow = true; entity.object.add(mesh);
            });
        });
    }
    applyVisibility(visible) {
        if (this.view.debugOptions?.renderLiveEntities === false || !this.view.visibility?.clustersForBounds) return;
        const bounds = new T.Box3(), sphere = new T.Sphere(), box = new T.Box3();
        for (const entity of [...this.entities.values(), ...this.gibs.values()]) {
            if (entity.object.parent === this.skyRoot || entity.state.components[Component.Light]) continue;
            const brush = entity.path?.startsWith('*') && this.brushModel(entity.path);
            if (brush) {
                const clusters = this.brushClusters(entity, brush, entity.object.matrixWorld, 'brushRenderClusters');
                const layer = this.view.inPvs(clusters, visible) ? 0 : 1;
                if (entity.brushLayer !== layer || entity.brushLayerToken !== entity.token) {
                    entity.object.traverse(object => object.layers.set(layer));
                    entity.brushLayer = layer; entity.brushLayerToken = entity.token;
                }
                continue;
            }
            bounds.makeEmpty();
            entity.object.traverse(object => {
                // Loading anchors and animated meshes expose their own sphere.
                const source = object.boundingSphere ?? object.geometry?.boundingSphere;
                if (!source) return;
                sphere.copy(source).applyMatrix4(object.matrixWorld);
                sphere.getBoundingBox(box); bounds.union(box);
            });
            const clusters = bounds.isEmpty() ? [] : this.view.visibility.clustersForBounds(bounds.min.toArray(), bounds.max.toArray());
            const layer = this.view.inPvs(clusters, visible) ? 0 : 1;
            entity.object.traverse(object => object.layers.set(layer));
        }
    }
    reconcileKarts() {
        for(const owner of this.entities.values()) {
            const key=`${owner.key}:kart`,state=kartVisualState(owner);let kart=this.gibs.get(key);
            if(!state){if(kart){this.remove(kart);this.gibs.delete(key);}continue;}
            const skin=state.components[Component.Appearance].skin;
            if(kart&&kart.state.components[Component.Appearance].skin!==skin){this.remove(kart);this.gibs.delete(key);kart=null;}
            if(!kart){kart={key,owner:owner.key,kart:true,path:kartModel,state,object:new T.Group(),resources:new Set(),token:0,pending:true};this.gibs.set(key,kart);this.pendingModels.add(kart);this.root.add(kart.object);}
            kart.state=state;
        }
        this.pump();
    }
    update(now) {
        const metrics = this.view.performanceOverlay?.details;
        const enabled = this.view.debugOptions?.renderLiveEntities !== false;
        this.root.visible = this.skyRoot.visible = enabled;
        if (enabled) {
            if (!this.root.parent) this.view.scene.add(this.root);
            if (!this.skyRoot.parent) this.view.skyScene?.add(this.skyRoot);
            if (this.visualsDirty) {
                this.visualsDirty = false;
                for (const entity of this.entities.values()) this.refreshVisual(entity);
                this.reconcileTauntProps();
            }
            if (this.visualsSuspended) {
                this.visualsSuspended = false;
                for (const entity of [...this.entities.values(), ...this.gibs.values()])
                    if (entity.ragdoll) entity.ragdoll.lastTime = this.timeline.animationTime(now);
            }
            this.updateEntities(now);
        } else {
            // Invisible roots still incur Three's recursive matrix updates.
            this.root.removeFromParent(); this.skyRoot.removeFromParent();
            this.visualsSuspended = true;
            this.deathTracesUsed = this.ambientSamples = 0;
            for (const key of ['movement', 'animation', 'sprites', 'physics', 'lighting']) metrics?.record(key, 0);
        }
        let stamp = metrics ? performance.now() : 0;
        this.sprays.update(this.timeline.time(now));
        if (metrics) { metrics.record('sprays', performance.now() - stamp); stamp = performance.now(); }
        if (this.particles.enabled) {
            this.particles.reconcileModels(this.gibs);
            this.particles.update(this.timeline.animationTime(now));
        } else this.particles.suspend(this.timeline.animationTime(now));
        if (metrics) { metrics.record('particles', this.particles.enabled ? performance.now() - stamp : 0); stamp = performance.now(); }
        const audioTime = this.timeline.animationTime(now);
        this.announcer?.frame(audioTime, !!(this.view.audioOutput?.enabled && this.view.audioOutput.allowed && this.view.audioOutput.context?.state === 'running'));
        this.sounds?.frame(audioTime, this.view.camera, this.entities);
        if (metrics) metrics.record('sound', performance.now() - stamp);
    }
    updateEntities(now) {
        const metrics = this.view.performanceOverlay?.details;
        let stamp = metrics ? performance.now() : 0;
        let movementMs = 0, animationMs = 0, lightingMs = 0, physicsMs = 0;
        this.reconcileKarts();
        if (metrics) { physicsMs += performance.now() - stamp; stamp = performance.now(); }
        const time = this.timeline.time(now);
        let deathTraceBudget = 256;
        const deathTrace = (a,b) => deathTraceBudget-- > 0 ? this.view.visibility?.trace(a,b) : { fraction: 1 };
        for (const entity of this.entities.values()) {
            if (metrics) stamp = performance.now();
            const transform = interpolateTransform(entity.state.history, time) ?? entity.state.components[Component.Transform];
            entity.object.position.fromArray(transform.position);
            const playerState = entity.state.components[Component.Player];
            this.view.angles(entity.object, playerState ? [0, transform.angles[1], 0] : transform.angles);
            const trail = entity.state.components[Component.Sprite]?.trail;
            const trailSky = trail && (trail.skyboxScale !== 1 || trail.skyboxOrigin.some(v => v !== 0));
            const inSky = entity.state.components[Component.Teleport] ? false : entity.path?.startsWith("*") ? (this.brushModel(entity.path)?.inSky ?? true) : trailSky || !!this.view.skyDefinition && this.view.skyArea >= 0 && this.view.visibility?.areaAt(transform.position) === this.view.skyArea;
            const parentRoot = inSky ? this.skyRoot : this.root;
            entity.inSky = inSky;
            if (entity.object.parent !== parentRoot) parentRoot.add(entity.object);
            const appearance = entity.state.components[Component.Appearance];
            entity.object.scale.setScalar(appearance.scale);
            if (metrics) { movementMs += performance.now() - stamp; stamp = performance.now(); }
            this.updateLight(entity);
            if (metrics) { lightingMs += performance.now() - stamp; stamp = performance.now(); }
            if (entity.rig && entity.boneMergedLast) entity.rig.bones.forEach((bone, i) => {
                bone.position.fromArray(entity.rig.data.bones[i].position);
                bone.quaternion.fromArray(entity.rig.data.bones[i].quaternion); bone.scale.setScalar(1);
            });
            entity.boneMergedLast = false;
            if (entity.animation && !entity.state.components[Component.Ragdoll]) {
                const player = entity.state.components[Component.Player];
                const weapon = player && this.entities.get(`${player.weaponIndex}:${player.weaponGeneration}`);
                const classname = this.dictionary.get(`2:${weapon?.state.components[Component.Appearance]?.netClass}`) ?? "";
                entity.animation.update(entity.state, this.timeline.animationTime(now), classname);
            }
            if (metrics) { animationMs += performance.now() - stamp; stamp = performance.now(); }
            entity.object.visible = !(appearance.effects & 32) && !entity.animation?.waitingForClips
                && !(appearance.viewerSteamId && appearance.viewerSteamId === this.view.viewerSteamId); // EF_NODRAW / unposed player rig
            const death = entity.state.components[Component.Ragdoll];
            if (death) {
                entity.ragdoll ??= new RagdollMotion(entity, this.timeline.animationTime(now), this.entities);
                entity.ragdoll.update(this.timeline.animationTime(now), deathTrace);
                if (death.flags & 1 && entity.rig && !entity.gibsStarted) this.createGibs(entity, death);
                entity.object.visible = !(death.flags & (1 | 16 | 64)) && entity.ragdoll.age <= 15;
            }
            if (metrics) { physicsMs += performance.now() - stamp; stamp = performance.now(); }
            for (const mesh of entity.object.children) if (mesh.material) updatePaintMaterial(mesh.material, entity.state.components[Component.Effects]);
            const cloaked = !!(entity.state.components[Component.Player]?.state & 64);
            if (entity.color !== appearance.color || entity.renderMode !== appearance.renderMode || entity.cloaked !== cloaked) {
                entity.cloaked = cloaked;
                entity.color = appearance.color; entity.renderMode = appearance.renderMode;
                for (const mesh of entity.object.children) if (mesh.material && !entity.sprite && !entity.worldText) {
                    applyRenderState(mesh.material, { renderMode: appearance.renderMode,
                        color: [appearance.color & 255, appearance.color >>> 8 & 255, appearance.color >>> 16 & 255],
                        alpha: appearance.color >>> 24, cloaked });
                }
            }
            if (metrics) movementMs += performance.now() - stamp;
        }
        if (metrics) stamp = performance.now();
        this.skyRoot.updateMatrixWorld(true);
        this.parenting.update(this.entities, this.root, time);
        for (const entity of this.entities.values()) if (entity.worldText) updateWorldText(entity, this.view.camera);
        if (metrics) { metrics.record('movement', movementMs + performance.now() - stamp); stamp = performance.now(); }
        for (const entity of this.entities.values()) entity.sprite?.update(entity,
            entity.state.components[Component.Sprite] ?? {}, entity.state.components[Component.Appearance], time, this.view, this.entities);
        if (metrics) { metrics.record('sprites', performance.now() - stamp); stamp = performance.now(); }
        for (const [key, gib] of this.gibs) {
            if(gib.kart) {
                const owner=this.entities.get(gib.owner);
                if(owner)poseKart(gib,owner,this.parenting);
                continue;
            }
            gib.ragdoll.update(this.timeline.animationTime(now), deathTrace);
            gib.object.updateMatrixWorld(true);
            if (gib.ragdoll.age > 15) { this.remove(gib); this.gibs.delete(key); }
        }
        if (metrics) { metrics.record('physics', physicsMs + performance.now() - stamp); stamp = performance.now(); }
        this.deathTracesUsed = 256 - Math.max(0, deathTraceBudget);
        const boundsStarted = this.measureNow();
        for (const entity of [...this.entities.values(), ...this.gibs.values()]) if (entity.animatedBounds)
            for (const bounds of entity.animatedBounds) bounds.update();
        const boundsMs = this.measureNow() - boundsStarted;
        this.timings.record("animatedBoundsMs", boundsMs);
        if (metrics) { metrics.record('animation', animationMs + boundsMs); stamp = performance.now(); }
        this.updateAmbient(now);
        if (metrics) metrics.record('lighting', lightingMs + performance.now() - stamp);
        if (this.entities.size && this.view.lighting) this.view.lighting.needsShadowUpdate = true;
    }
    createGibs(entity, death) {
        entity.gibsStarted = true;
        if ((this.timeline.animationTime(this.now()) - death.startTime) > 15000) return;
        const paths = entity.rig.data.breakModels ?? [];
        if (!paths.length) { this.view.diagnostic?.(`${entity.path}: no studio breakmodels available for gibs`); return; }
        for (const [i, path] of paths.slice(0, 8).entries()) {
            if (this.gibs.size >= 64) break;
            const key = `gib:${entity.key}:${i}`, state = { ...entity.state, key, components: {
                [Component.Transform]: { ...entity.state.components[Component.Transform] },
                [Component.Appearance]: { ...entity.state.components[Component.Appearance], effects: 0 },
                [Component.Ragdoll]: { ...death, flags: 0, velocity: gibVelocity(death, i, entity.state.generation), force: [0,0,0] },
            } };
            const gib = { key, owner: entity.key, gibBurning: !!(death.flags & 2), path, state, object: new T.Group(), resources: new Set(), token: 0, pending: true };
            gib.object.position.copy(entity.object.position).add(new T.Vector3(0,0,35)); gib.object.quaternion.copy(entity.object.quaternion);
            (entity.object.parent ?? this.root).add(gib.object); this.gibs.set(key, gib); this.pendingModels.add(gib);
            gib.ragdoll = new RagdollMotion(gib, this.timeline.animationTime(this.now()), this.entities);
        }
        this.pump();
    }
    updateAmbient(now) {
        this.ambientSamples = 0;
        if (!this.view.lighting || !this.entities.size) return;
        const deadline = performance.now() + 0.5;
        this.lightIterator ??= this.entities.values();
        this.lightPosition ??= new T.Vector3();
        let visited = 0, sampled = 0;
        while (visited < this.entities.size && sampled < 8 && performance.now() < deadline) {
            const next = this.lightIterator.next();
            if (next.done) { this.lightIterator = this.entities.values(); continue; }
            visited++;
            const entity = next.value;
            if (!entity.ambient || now < (entity.nextLight ?? 0)) continue;
            this.lightPosition.copy(entity.lightOrigin).applyMatrix4(entity.object.matrixWorld);
            this.view.lighting.sampleAmbient(this.lightPosition.toArray(), now / 1000, entity.ambient);
            entity.nextLight = now + 250; sampled++; this.ambientSamples = sampled;
        }
    }
    updateLight(entity) {
        const state = entity.state.components[Component.Light], system = this.view.lighting;
        if (!state || !system) return;
        if (entity.light && Number(!!entity.light.object.isSpotLight) !== state.kind) this.clearVisual(entity);
        if (!entity.light) {
            if (system.dynamic.length >= 32) return;
            this.clearVisual(entity);
            const object = state.kind ? new T.SpotLight() : new T.PointLight();
            object.decay = 0;
            entity.object.add(object);
            if (object.isSpotLight) { object.target.position.set(1, 0, 0); entity.object.add(object.target); }
            entity.light = { object, flags: 0, enabled: false, style: 0, base: Math.PI };
            system.dynamic.push(entity.light);
        }
        const light = entity.light;
        light.enabled = !!(state.flags & 1); light.style = state.style;
        light.flags = (state.flags & 2 ? 2 : 0) | (state.flags & 4 ? 1 : 0);
        light.object.color.setRGB(...[0, 8, 16].map(shift => (state.color >>> shift & 255) / 255 * 2 ** state.exponent));
        light.object.distance = state.radius;
        if (light.object.isSpotLight) light.object.angle = state.fov * Math.PI / 360;
        system.refreshMasks();
    }
    dispose() {
        if (this.closed) return;
        this.closed = true;
        this.bodygroups.clear();
        this.particles.dispose();
        this.announcerAudio?.dispose(); this.announcer?.clear(); this.sounds?.dispose();
        this.sprays.dispose();
        for (const entity of this.entities.values()) this.remove(entity);
        this.entities.clear();
        for (const gib of this.gibs.values()) this.remove(gib); this.gibs.clear();
        this.modelLoads.clear(); this.tauntPropModels.clear(); this.clips.dispose(); this.root.removeFromParent(); this.skyRoot.removeFromParent(); this.loadingSpinners.dispose();
        for (const [object, visible] of this.hidden) object.visible = visible;
        for (const item of this.view.brushPrototypes ?? this.view.meshes ?? []) item.liveHidden = false;
        this.hidden.clear();
        if (this.mapLights && this.view.lighting) {
            this.view.lighting.dynamic = this.mapLights; this.view.lighting.refreshMasks();
        }
    }
}
